import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CampaignService } from "@gen/tracker/v1/campaign_connect";
import type { CampaignListItem } from "@gen/tracker/v1/campaign_pb";
import { DashboardService } from "@gen/tracker/v1/dashboard_connect";
import type { PnLPoint } from "@gen/tracker/v1/dashboard_pb";
import { DateRange } from "@gen/tracker/v1/common_pb";

import { createClient } from "@/lib/connect";
import { campaignKeys } from "@/lib/services/campaign-service";

const campaigns = createClient(CampaignService);
const dashboard = createClient(DashboardService);

/** «Сегодня» refreshes on its own; a buyer leaves it open. */
export const TODAY_POLL_MS = 60_000;

/** How many campaigns each of «Лучшие» and «Худшие» shows (доска Today). */
export const LEADERS = 7;

/** How many campaigns «Сливают» and «Нет кликов» each bring into «Требует внимания». */
export const FLAGGED = 5;

/**
 * The numbers by HOUR over [from, to] in `tz` — the one source of the strip,
 * «к вчера к 15:53» and the running chart (the same reader as every report).
 * Under «Мои» only the caller's campaigns; with none, no request at all.
 */
export function useHourlyTrend(from: string, to: string, tz: string, ids: string[] | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["dashboard", "hourly", from, to, tz, ids ?? null] as const,
    enabled,
    refetchInterval: TODAY_POLL_MS,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<PnLPoint[]> =>
      (await dashboard.getPnLTrend({ dateRange: new DateRange({ from, to, timezone: tz }), hourly: true, campaignIds: ids ?? [] })).points,
  });
}

export interface TodayCampaigns {
  best: CampaignListItem[];
  worst: CampaignListItem[];
  losing: CampaignListItem[];
  noClicks: CampaignListItem[];
  /**
   * Under «Мои», the caller's own campaigns outside the archive, sorted — the
   * ids the hourly trend is asked over. Undefined under «Все».
   */
  ids?: string[];
}

/**
 * The day's campaigns, ranked and flagged by the server over today — ONE
 * question (GetDayBoards): the seven that made the most, the seven that lost
 * the most, the ones losing money (quick «Сливают») and the ones that spend
 * without a click, each cut by the server from one count of the day; under
 * «Мои» (`mine` — the caller as the server knows him, never a sub the browser
 * names) also the caller's own campaign ids. Nothing is sorted or judged in
 * the browser.
 */
export function useTodayCampaigns(today: string, tz: string, mine: boolean) {
  return useQuery({
    queryKey: [...campaignKeys.all, "today", today, tz, mine] as const,
    refetchInterval: TODAY_POLL_MS,
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<TodayCampaigns> => {
      const res = await campaigns.getDayBoards({
        statsRange: new DateRange({ from: today, to: today, timezone: tz }),
        mine,
        leaders: LEADERS,
        flagged: FLAGGED,
      });
      return { best: res.best, worst: res.worst, losing: res.losing, noClicks: res.noClicks, ids: mine ? res.ownIds : undefined };
    },
  });
}
